import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

/**
 * ToastProvider / useToast (audit 2026-09-20, punto #10).
 *
 * Sostituisce i 43 `alert()` nativi sparsi nell'app: un alert() blocca il
 * thread, non si può stilizzare, su mobile mostra il dominio in cima
 * ("noleggioincloud.app dice:") e nel momento in cui l'utente sta facendo
 * qualcosa — cancellare un veicolo, annullare una prenotazione — l'interfaccia
 * sembra un sito del 2004.
 *
 * USO:
 *   const toast = useToast();
 *   toast.success("Salvato!");
 *   toast.error("Errore durante il salvataggio.");
 *   toast.info("...");
 */
const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const COLORS = {
  success: { bg: '#f0fdf4', border: '#bbf7d0', icon: '#16a34a', text: '#14532d' },
  error:   { bg: '#fef2f2', border: '#fecaca', icon: '#dc2626', text: '#7f1d1d' },
  info:    { bg: '#eff6ff', border: '#bfdbfe', icon: '#2563eb', text: '#1e3a5f' },
  warning: { bg: '#fffbeb', border: '#fde68a', icon: '#d97706', text: '#78350f' },
};

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const show = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++idCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const api = useRef({
    success: (msg, duration) => show(msg, 'success', duration),
    error:   (msg, duration) => show(msg, 'error', duration ?? 6000),
    info:    (msg, duration) => show(msg, 'info', duration),
    warning: (msg, duration) => show(msg, 'warning', duration),
    dismiss,
  }).current;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'fixed',
          bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 8,
          width: 'min(420px, calc(100vw - 24px))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => {
          const Icon = ICONS[t.type] || Info;
          const c = COLORS[t.type] || COLORS.info;
          return (
            <div
              key={t.id}
              role={t.type === 'error' ? 'alert' : 'status'}
              style={{
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                background: c.bg,
                border: `1px solid ${c.border}`,
                color: c.text,
                borderRadius: 12,
                padding: '12px 14px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                fontSize: 14,
                lineHeight: 1.4,
                animation: 'noc-toast-in 0.18s ease-out',
              }}
            >
              <Icon size={18} color={c.icon} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{t.message}</div>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Chiudi notifica"
                style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.6, padding: 2 }}
              >
                <X size={15} />
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes noc-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Non blocca la build/i test se un componente viene montato fuori dal
    // provider (es. in isolamento in un test) — degrada su console.log
    // invece di lanciare, ma segnala comunque il problema di setup.
    console.warn('useToast() chiamato fuori da <ToastProvider>. Nessun provider trovato nell\'albero.');
    return { success: console.log, error: console.error, info: console.log, warning: console.warn, dismiss: () => {} };
  }
  return ctx;
}
